import { useMsal } from '@azure/msal-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { CompanyLogo } from '@/components/company/CompanyLogo';
import { isAuthConfigured, loginRequest } from '@/auth/config';
import {
  INVITE_LOGIN_STATE,
  INVITE_RESUME_PATH,
  clearSigningIn,
  markSigningIn,
  readPendingInvite,
  savePendingInvite,
} from '@/auth/pendingInvite';
import {
  InviteGoneError,
  InviteRefusedError,
  InviteSignInInactiveError,
  InviteSignInNeededError,
  useAcceptInvite,
  usePrepareInviteSignIn,
  usePublicInvite,
} from '@/routes/invite/usePublicInvite';

/**
 * The invitee's page (M2-20, pass B): `/invite/{token}` from the email.
 *
 * ⚠️ Routed OUTSIDE `RequireAuth` and `AppLayout` (see `App.tsx`): the person
 * has no user row yet, so the shell's `/me` and `/account` would 403. The
 * anonymous reads go through `publicClient`; the accept carries its bearer
 * token by hand for the reason in `useAcceptInvite`.
 *
 * The steps, as the API designed them (api/CONVENTIONS.md, M2-20):
 *
 * 1. Show the company and the role (`GET`).
 * 2. **Continue** asks the API to make sure the invited address has a sign-in
 *    (`POST /sign-in`). A 503 is said plainly, with a way on for someone who
 *    already has one.
 * 3. The ordinary sign-in. Someone new sets a password through "Forgot
 *    password?" and a code emailed to the invited address; the page says so
 *    before they go, because nothing at Entra will.
 * 4. Back here (`AuthProvider` returns the tab to `/invite`), `POST /accept`.
 * 5. Each outcome says its own thing: in, or the API's sentence (a different
 *    email, a sign-in that already belongs to a company, a federated sign-in,
 *    no email claim), the page's own sentence for the body-less 403 (a sign-in
 *    that is a deactivated user elsewhere), or the one dead-link page for
 *    every 404.
 *
 * The token leaves the address bar on arrival (`auth/pendingInvite.ts`).
 */
export function AcceptInvite() {
  const { t } = useTranslation();
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const { token: fromUrl } = useParams<{ token: string }>();

  // The token moves out of the URL before anything else happens. If storage
  // refuses it, the page keeps working from the URL (and sign-in, which needs
  // the same storage, would not have worked either).
  const [kept] = useState(() => (fromUrl ? savePendingInvite(fromUrl) : false));
  const pending = readPendingInvite();
  // Held for the life of the page, not re-read from storage: accepting clears
  // the stored copy, and the page must still show what happened (found by
  // the success test — it fell through to "open the link from your email").
  const [held] = useState(() => fromUrl ?? pending?.token);
  const token = fromUrl ?? held;
  useEffect(() => {
    if (fromUrl && kept) navigate(INVITE_RESUME_PATH, { replace: true });
  }, [fromUrl, kept, navigate]);

  const invite = usePublicInvite(token);
  const prepare = usePrepareInviteSignIn(token);
  const accept = useAcceptInvite(token, instance);
  const signedIn = accounts.length > 0;

  function signIn(prompt?: 'login') {
    markSigningIn();
    void instance.loginRedirect({
      ...loginRequest,
      state: INVITE_LOGIN_STATE,
      ...(prompt ? { prompt } : {}),
    });
  }

  function proceed() {
    if (!isAuthConfigured) return;
    if (signedIn) accept.mutate({});
    else signIn();
  }

  // Back from Entra with a sign-in: accept, once.
  const returnedSignedIn = pending?.signingIn === true && signedIn;
  useEffect(() => {
    if (returnedSignedIn && accept.isIdle) {
      clearSigningIn();
      accept.mutate({ afterSignIn: true });
    }
  }, [returnedSignedIn, accept]);

  // Entra needs the person again before it hands out a token — unless they
  // have only just signed in: then another redirect would come back to the
  // same error, round and round, so the failure is shown instead and Try
  // again (a person's click) may send them once more.
  const signInAgain = accept.error instanceof InviteSignInNeededError && accept.variables?.afterSignIn !== true;
  useEffect(() => {
    if (signInAgain) signIn();
    // Keyed on the decision alone: `signIn` reads only `instance` and storage.
  }, [signInAgain]);

  if (!token) {
    return (
      <Page>
        <Explained title={t('invite.goneTitle')}>{t('invite.noToken')}</Explained>
      </Page>
    );
  }

  if (invite.isPending) {
    return (
      <Page>
        <LoadingState label={t('invite.loading')} className="justify-center" />
      </Page>
    );
  }

  const gone = [invite.error, prepare.error, accept.error].some((e) => e instanceof InviteGoneError);
  if (gone) {
    return (
      <Page>
        <Explained title={t('invite.goneTitle')}>{t('invite.goneDescription')}</Explained>
      </Page>
    );
  }

  if (invite.isError) {
    return (
      <Page>
        <ErrorState
          title={t('invite.loadErrorTitle')}
          description={t('invite.loadErrorDescription')}
          onRetry={() => void invite.refetch()}
        />
      </Page>
    );
  }

  const { companyName, role, logoPath } = invite.data;
  const company = (
    <div className="flex flex-col items-center gap-3">
      <CompanyLogo
        path={logoPath}
        alt=""
        frameClassName="flex h-14 items-center"
        className="h-auto max-h-14 w-auto max-w-[12rem] object-contain"
        testId="invite-logo"
      />
      <p className="text-base font-semibold break-words" data-testid="invite-company">
        {companyName}
      </p>
    </div>
  );

  if (accept.isSuccess) {
    return (
      <Page>
        {company}
        <Joined companyName={accept.data.companyName} />
      </Page>
    );
  }

  const acceptRefusal =
    accept.error instanceof InviteRefusedError
      ? accept.error.message
      : accept.error instanceof InviteSignInInactiveError
        ? t('invite.signInInactive')
        : null;
  const prepareRefusal = prepare.error instanceof InviteRefusedError ? prepare.error : null;
  const busy = prepare.isPending || accept.isPending || returnedSignedIn;
  const signInAbandoned = pending?.signingIn === true && !signedIn;

  return (
    <Page>
      {company}
      <section aria-labelledby="invite-heading" className="space-y-4 text-center">
        <h1 id="invite-heading" className="text-xl font-semibold tracking-tight">
          {t('invite.heading', { company: companyName })}
        </h1>
        <p className="text-sm" data-testid="invite-role">
          {t('invite.asRole', {
            company: companyName,
            role: t(`invite.roleWithArticle.${role}`, { defaultValue: role }),
          })}
        </p>

        {acceptRefusal ? (
          <Notice testId="invite-refused">
            <p>{acceptRefusal}</p>
            <Button type="button" className="w-full sm:w-auto" onClick={() => signIn('login')}>
              {t('invite.signInDifferent')}
            </Button>
          </Notice>
        ) : accept.isError && !signInAgain ? (
          <Notice testId="invite-accept-failed">
            <p>{t('invite.acceptFailed')}</p>
            <Button type="button" className="w-full sm:w-auto" onClick={() => accept.mutate({})}>
              {t('invite.tryAgain')}
            </Button>
          </Notice>
        ) : busy ? (
          <p role="status" className="text-sm text-[var(--color-muted-foreground)]">
            {accept.isPending || returnedSignedIn ? t('invite.accepting') : t('invite.preparing')}
          </p>
        ) : (
          <>
            {signInAbandoned ? (
              <p role="status" className="text-sm" data-testid="invite-sign-in-unfinished">
                {t('invite.signInUnfinished')}
              </p>
            ) : null}
            <div className="space-y-2 rounded-md border p-3 text-left text-sm" data-testid="invite-how">
              <p className="font-medium">{t('invite.howTitle')}</p>
              <p>{t('invite.howExisting')}</p>
              <p>{t('invite.howNew')}</p>
            </div>

            {prepareRefusal ? (
              <Notice testId="invite-prepare-refused">
                <p>{prepareRefusal.message}</p>
                {/* A 503 (the directory isn't set up, or Graph failed) still
                    leaves someone who already HAS a sign-in a way in; a 409
                    (an address no sign-in can have) does not. */}
                {prepareRefusal.status === 503 && isAuthConfigured ? (
                  <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={proceed}>
                    {t('invite.signInExisting')}
                  </Button>
                ) : null}
              </Notice>
            ) : prepare.isError ? (
              <Notice testId="invite-prepare-failed">
                <p>{t('invite.prepareFailed')}</p>
              </Notice>
            ) : null}

            {prepareRefusal?.status === 409 ? null : (
              <Button
                type="button"
                className="w-full sm:w-auto"
                onClick={() => prepare.mutate(undefined, { onSuccess: proceed })}
              >
                {t('invite.continue')}
              </Button>
            )}
            {prepare.isSuccess && !isAuthConfigured ? (
              <p className="text-sm text-[var(--color-muted-foreground)]">{t('signIn.notConfigured')}</p>
            ) : null}
          </>
        )}
      </section>
    </Page>
  );
}

/** Accepted: in, with focus on the sentence that says so. */
function Joined({ companyName }: { companyName: string }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => ref.current?.focus(), []);
  return (
    <section aria-labelledby="invite-joined" className="space-y-4 text-center">
      <h1 id="invite-joined" ref={ref} tabIndex={-1} className="text-xl font-semibold tracking-tight focus:outline-none">
        {t('invite.joinedTitle', { company: companyName })}
      </h1>
      <Button asChild className="w-full sm:w-auto">
        <Link to="/">{t('invite.goToApp')}</Link>
      </Button>
    </section>
  );
}

/** A refusal or failure, announced and focused: the button the person pressed is gone. */
function Notice({ testId, children }: { testId: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => ref.current?.focus(), []);
  return (
    <div
      ref={ref}
      tabIndex={-1}
      role="alert"
      data-testid={testId}
      className="space-y-3 rounded-md border border-[var(--color-destructive)] p-3 text-sm focus:outline-none"
    >
      {children}
    </div>
  );
}

function Page({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <main className="mx-auto max-w-md space-y-6 px-4 py-10">{children}</main>
    </div>
  );
}

function Explained({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-labelledby="invite-explained" className="space-y-3 pt-6 text-center">
      <h1 id="invite-explained" className="text-xl font-semibold tracking-tight">
        {title}
      </h1>
      <p className="mx-auto max-w-prose text-sm text-[var(--color-muted-foreground)]">{children}</p>
    </section>
  );
}
